import { Card, Descriptions, Button, Space, message, Modal, Tag } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getApp, deleteApp } from '@/api/apps';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
// 导入配套图标，提升交互视觉
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  InfoCircleOutlined 
} from '@ant-design/icons';

/**
 * App 详情页面
 */
export const AppDetail = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const developerId = getCurrentDeveloperId();
  const queryClient = useQueryClient();

  const appQuery = useQuery({
    queryKey: ['app', developerId, appId],
    queryFn: () => {
      if (!appId) {
        throw new Error('App ID 不能为空');
      }
      return getApp(appId);
    },
    enabled: !!appId && !!developerId,
  });

  const app = appQuery.data;
  const loading = appQuery.isLoading;
  const error = appQuery.error;

  const handleEdit = (): void => {
    if (appId) {
      navigate(`/apps/${appId}/edit`);
    }
  };

  /**
   * 处理删除 App
   */
  const handleDelete = (): void => {
    if (!appId || !app) return;

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除 App "${app.name}" 吗？此操作不可恢复。`,
      okType: 'danger',
      okText: '删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteApp(appId);
          message.success('App 删除成功');
          await queryClient.invalidateQueries({ 
            queryKey: ['apps', developerId] 
          });
          navigate('/apps');
        } catch (error) {
           message.error(error instanceof Error ? error.message : '删除失败，请重试。');
        }
      },
    });
  };

  const handleBack = (): void => {
    navigate('/apps');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载 App 详情，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  if (!app) {
    return (
      <ErrorAlert 
        message="未找到 App" 
        description="指定的 App 不存在或已被删除"
        className="mb-8"
      />
    );
  }

  // 优化映射表：增加Tag颜色，和AppList表格标签配色统一
  const sdkCategoryMap: Record<string, { label: string; color: string }> = {
    smart_home: { label: '智能生活', color: 'blue' },
    wearable: { label: '可穿戴设备', color: 'purple' },
    healthcare: { label: '健康医疗', color: 'green' },
    industrial: { label: '工业 IoT', color: 'orange' },
  };

  const channelMap: Record<string, { label: string; color: string }> = {
    official: { label: '官方渠道', color: 'primary' },
    beta: { label: '测试渠道', color: 'warning' },
    enterprise: { label: '企业定制', color: 'success' },
  };

  // 获取分类/渠道的标签配置
  const categoryConfig = sdkCategoryMap[app.sdk_category] || { label: app.sdk_category, color: 'gray' };
  const channelConfig = channelMap[app.channel] || { label: app.channel, color: 'default' };

  return (
    <div className="w-full space-y-8">
      {/* 页面标题+操作区 - 优化样式、间距、文字层级，适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <InfoCircleOutlined className="text-blue-500" />
            App 详情
          </h1>
          <p className="text-gray-500 mt-1">查看 {app.name} 的详细配置信息，支持编辑和删除操作。</p>
        </div>
        <Space size="middle">
          <Button 
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回列表
          </Button>
          <Button 
            type="primary" 
            onClick={handleEdit}
            icon={<EditOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            编辑 App
          </Button>
          <Button 
            danger 
            onClick={handleDelete}
            icon={<DeleteOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            删除 App
          </Button>
        </Space>
      </div>

      {/* 基本信息卡片 - 适配Antd5.x，替换废弃属性，保留所有美化样式 */}
      <Card 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        // 用style替代废弃的bodyStyle，统一控制卡片内边距和标题样式
        style={{ 
          padding: '24px',
          '--ant-card-head-font-size': '18px',
          '--ant-card-head-font-weight': '600',
          '--ant-card-head-color': '#1D2129',
          '--ant-card-border-radius': '8px' // 统一项目圆角规范
        } as React.CSSProperties}
      >
        <Descriptions
          column={1}
          size="middle"
          labelStyle={{ 
            width: '140px', 
            fontWeight: '600', 
            color: '#667085',
            textAlign: 'left'
          }}
          contentStyle={{ color: '#1D2129' }}
          // 优化描述列表行间距，添加类型断言避免TS报错
          style={{ 
            '--ant-descriptions-item-margin-bottom': '16px',
            fontSize: '15px'
          } as React.CSSProperties}
        >
          <Descriptions.Item label="App 名称">{app.name}</Descriptions.Item>
          <Descriptions.Item label="App ID">
            <span className="font-mono">{app.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="SDK 类别">
            {/* 移除Tag废弃的size属性，用className控制尺寸，保留颜色和视觉 */}
            <Tag color={categoryConfig.color} className="text-base py-1 px-2">
              {categoryConfig.label}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="渠道标识符">
            <Tag color={channelConfig.color} className="text-base py-1 px-2">
              {channelConfig.label}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {new Date(app.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {new Date(app.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};